import { useState, useEffect } from 'react';
import api from '@/services/api';

export interface VoicePreferences {
  voiceNoiseSuppressionEnabled: boolean | null;
  voiceEchoCancellationEnabled: boolean | null;
  voiceAutoGainControlEnabled: boolean | null;
  voiceActivationMode: 'VOICE_ACTIVITY' | 'PUSH_TO_TALK' | 'ALWAYS_ON' | null;
  voiceActivationThreshold: number | null;
  muteOnJoin: boolean | null;
}

export const useVoicePreferences = () => {
  const [preferences, setPreferences] = useState<VoicePreferences>({
    voiceNoiseSuppressionEnabled: null,
    voiceEchoCancellationEnabled: null,
    voiceAutoGainControlEnabled: null,
    voiceActivationMode: null,
    voiceActivationThreshold: null,
    muteOnJoin: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch current preferences
  const fetchPreferences = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/auth/voice-preferences');

      setPreferences(response.data);
    } catch (err: any) {
      console.error('Error fetching voice preferences:', err);
      setError(err.response?.data?.error || 'Failed to fetch voice preferences');
    } finally {
      setLoading(false);
    }
  };

  // Update preferences
  const updatePreferences = async (newPreferences: Partial<VoicePreferences>) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.put('/auth/voice-preferences', newPreferences);

      setPreferences(response.data.voicePreferences);
      return true;
    } catch (err: any) {
      console.error('Error updating voice preferences:', err);
      setError(err.response?.data?.error || 'Failed to update voice preferences');
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      fetchPreferences();
    }
  }, []);

  return {
    preferences,
    loading,
    error,
    updatePreferences,
    refreshPreferences: fetchPreferences,
  };
};
